import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Sites() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [sites, setSites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", type: "depot", address: "" });

  const load = async () => setSites((await api.get("/sites")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/sites", form); toast.success("Site créé"); setShowForm(false); setForm({name:"",type:"depot",address:""}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/sites/${id}`); toast.success("Supprimé"); load();
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Sites" subtitle="Hôpitaux · Cliniques · Dépôts" testId="sites-header"
        actions={canEdit && <button data-testid="new-site-btn" className="btn-primary flex items-center gap-2" onClick={()=>setShowForm(!showForm)}><Plus size={14}/>Nouveau site</button>}
      />

      <div className="surface bg-[var(--primary-soft)] border-[var(--primary)]/30 p-4 text-sm space-y-2">
        <div className="font-semibold text-[var(--primary-dark)]">💡 À quoi servent les sites ?</div>
        <p className="text-[var(--text)]">Un <b>site</b> = un emplacement physique où vous gérez du stock. Pour la plupart des pharmacies, <b>1 seul site suffit</b> (votre officine).</p>
        <ul className="text-xs text-[var(--text-muted)] space-y-1 list-disc list-inside">
          <li><b>Dépôt</b> — votre pharmacie principale (par défaut)</li>
          <li><b>Hôpital / Clinique</b> — si vous gérez le stock d'un partenaire</li>
          <li>Plusieurs sites = transferts possibles entre eux + suivi du stock par lieu</li>
        </ul>
      </div>
      {showForm && (
        <Surface title="Nouveau site">
          <form onSubmit={submit} className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
            <div><label className="label-mono block mb-1.5">Nom</label><input data-testid="site-name" required placeholder="Ex: Pharmacie Plateau" className="input-base" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
            <div>
              <label className="label-mono block mb-1.5">Type</label>
              <select data-testid="site-type" className="input-base" value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})}>
                <option value="depot">Dépôt (officine)</option>
                <option value="hopital">Hôpital</option>
                <option value="clinique">Clinique</option>
              </select>
            </div>
            <div><label className="label-mono block mb-1.5">Adresse</label><input className="input-base" placeholder="Ex: 12 Avenue Pompidou, Dakar" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/></div>
            <div className="col-span-full flex gap-2"><button data-testid="submit-site" type="submit" className="btn-primary">Créer</button><button type="button" className="btn-ghost" onClick={()=>setShowForm(false)}>Annuler</button></div>
          </form>
        </Surface>
      )}
      <Surface testId="sites-table">
        {sites.length === 0 ? <Empty/> : (
          <table className="data-table">
            <thead><tr><th>Nom</th><th>Type</th><th>Adresse</th><th>Créé</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {sites.map(s => (
                <tr key={s.id} data-testid={`site-row-${s.name}`}>
                  <td className="font-medium">{s.name}</td>
                  <td><span className="badge-info">{s.type}</span></td>
                  <td className="text-[var(--text-muted)]">{s.address || "—"}</td>
                  <td className="font-data text-[var(--text-muted)] text-xs">{s.created_at?.slice(0,10)}</td>
                  {canEdit && <td><button onClick={()=>remove(s.id)} className="text-[var(--text-muted)] hover:text-[var(--neon-pink)]"><Trash2 size={14}/></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
